import { useCallback, useEffect, useState } from 'react';
import { useClerk, useSignIn, useSignUp } from '@clerk/clerk-react';

const identifierMissingCodes = new Set([
  'form_identifier_not_found',
  'identifier_not_found',
  'form_param_nil',
]);

// Clerk rate-limits repeated code sends; gate resends behind a short cooldown
// so the button gives feedback instead of silently 429-ing. Also armed after
// the first send so the very first "Resend" can't fire instantly.
const RESEND_COOLDOWN_SECONDS = 30;

const firstClerkError = (error) => error?.errors?.[0];

const userMessage = (error, fallback) =>
  firstClerkError(error)?.longMessage || firstClerkError(error)?.message || fallback;

export const usePasswordlessEmailCode = ({ onAuthenticated }) => {
  const { isLoaded: signInLoaded, signIn } = useSignIn();
  const { isLoaded: signUpLoaded, signUp } = useSignUp();
  const clerk = useClerk();
  const [phase, setPhase] = useState('email');
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  // Resend affordance: `cooldown` counts down the seconds until another send is
  // allowed; `resent` confirms a fresh code went out (cleared once the user
  // edits the code or changes email).
  const [cooldown, setCooldown] = useState(0);
  const [resent, setResent] = useState(false);

  useEffect(() => {
    if (cooldown <= 0) return undefined;
    const id = setInterval(() => setCooldown((seconds) => (seconds <= 1 ? 0 : seconds - 1)), 1000);
    return () => clearInterval(id);
  }, [cooldown > 0]);

  const sendCode = useCallback(async () => {
    const normalizedEmail = email.trim().toLowerCase();
    if (!/^\S+@\S+\.\S+$/.test(normalizedEmail)) {
      setError('Enter a valid corporate email address.');
      return;
    }
    if (!signInLoaded || !signUpLoaded || !signIn || !signUp) return;

    setBusy(true);
    setError(null);
    try {
      try {
        const attempt = await signIn.create({ identifier: normalizedEmail });
        const factor = attempt.supportedFirstFactors?.find(
          (candidate) => candidate.strategy === 'email_code',
        );
        if (!factor?.emailAddressId) {
          throw new Error('This account does not support a one-time email code.');
        }
        await signIn.prepareFirstFactor({
          strategy: 'email_code',
          emailAddressId: factor.emailAddressId,
        });
        setPhase('sign-in-code');
      } catch (signInError) {
        const errorCode = firstClerkError(signInError)?.code;
        if (!identifierMissingCodes.has(errorCode)) throw signInError;
        // `legalAccepted: true` records consent up front so a Clerk instance
        // configured passwordless-but-require-legal doesn't strand the attempt
        // in `missing_requirements: ["legal_accepted"]` after the email is
        // verified. (A required PASSWORD still can't be satisfied here — that
        // needs the instance itself set to passwordless; see verifyCode.)
        await signUp.create({ emailAddress: normalizedEmail, legalAccepted: true });
        await signUp.prepareEmailAddressVerification({ strategy: 'email_code' });
        setPhase('sign-up-code');
      }
      setEmail(normalizedEmail);
      setResent(false);
      setCooldown(RESEND_COOLDOWN_SECONDS);
    } catch (sendError) {
      setError(userMessage(sendError, 'A one-time code could not be sent. Try again.'));
    } finally {
      setBusy(false);
    }
  }, [email, signIn, signInLoaded, signUp, signUpLoaded]);

  // Re-issue a code for the CURRENT phase without leaving the code screen. The
  // sign-in resource keeps its supportedFirstFactors after create(), so we can
  // re-prepare the same email_code factor; sign-up just re-prepares its own
  // verification. A guard on `cooldown` keeps this in step with the UI's timer.
  const resendCode = useCallback(async () => {
    if (phase !== 'sign-in-code' && phase !== 'sign-up-code') return;
    if (cooldown > 0 || busy) return;

    setBusy(true);
    setError(null);
    setResent(false);
    try {
      if (phase === 'sign-in-code') {
        const factor = signIn?.supportedFirstFactors?.find(
          (candidate) => candidate.strategy === 'email_code',
        );
        if (!factor?.emailAddressId) {
          throw new Error('This account does not support a one-time email code.');
        }
        await signIn.prepareFirstFactor({
          strategy: 'email_code',
          emailAddressId: factor.emailAddressId,
        });
      } else {
        await signUp.prepareEmailAddressVerification({ strategy: 'email_code' });
      }
      setResent(true);
      setCooldown(RESEND_COOLDOWN_SECONDS);
    } catch (resendError) {
      setError(userMessage(resendError, 'We could not resend the code. Try again in a moment.'));
    } finally {
      setBusy(false);
    }
  }, [phase, cooldown, busy, signIn, signUp]);

  const verifyCode = useCallback(async () => {
    if (!/^\d{6}$/.test(code.trim())) {
      setError('Enter the six-digit code from your email.');
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const result =
        phase === 'sign-in-code'
          ? await signIn.attemptFirstFactor({ strategy: 'email_code', code: code.trim() })
          : await signUp.attemptEmailAddressVerification({ code: code.trim() });

      if (result.status === 'complete' && result.createdSessionId) {
        await clerk.setActive({ session: result.createdSessionId });
        await onAuthenticated();
        return;
      }

      // The code itself WAS accepted — Clerk has marked the email verified —
      // but the attempt can't reach `complete` because the instance still
      // requires fields a passwordless flow never collects. Reporting "that
      // code could not be verified" here is wrong and sends users in circles.
      // Name the real blocker instead.
      const missingFields = result.missingFields || [];
      if (missingFields.includes('password')) {
        setError(
          'Your email is verified, but this environment still requires a password to finish sign-up. ' +
            'Passwordless access must be enabled on the Clerk instance before this invitation can be claimed.',
        );
        return;
      }
      setError(
        'Your email is verified, but the workspace could not be opened automatically. ' +
          'Try “Sign in instead” below, or contact support.',
      );
    } catch (verifyError) {
      setError(userMessage(verifyError, 'That code could not be verified. Try again.'));
    } finally {
      setBusy(false);
    }
  }, [clerk, code, onAuthenticated, phase, signIn, signUp]);

  const changeEmail = useCallback(() => {
    setPhase('email');
    setCode('');
    setError(null);
    setResent(false);
    setCooldown(0);
  }, []);

  // Wrap setCode so typing a fresh digit clears the stale "code resent"
  // confirmation, matching the intent to enter the newest code.
  const updateCode = useCallback((next) => {
    setResent(false);
    setCode(next);
  }, []);

  return {
    step: phase === 'email' ? 'email' : 'code',
    email,
    setEmail,
    code,
    setCode: updateCode,
    busy,
    error,
    sendCode,
    verifyCode,
    resendCode,
    resendCooldown: cooldown,
    resent,
    changeEmail,
    ready: signInLoaded && signUpLoaded,
  };
};
